import { reactive } from 'vue'
import { getRequestContext } from './storeContext'
import { _register } from './storeRegistry'

/**
 * Creates a module's reactive state container.
 *
 * This is the one deliberate deviation from AuthorBase, whose `state.ts` files export a
 * bare `reactive()` singleton (`export default reactive(state)`). AuthorBase is SPA/Tauri
 * only; two of Booksprout's four apps are SSR, where a module-scope singleton is shared by
 * every concurrent request. So state is reached through a *function* instead:
 *
 *   // store/state.ts
 *   export const defaultState = (): SystemState => ({ ... })
 *   export default defineState('system', defaultState)
 *
 *   // store/getters.ts
 *   import state from './state'
 *   export default { get meta () { return state().meta } }
 *
 * In the browser that resolves to a single module-scope singleton, i.e. AuthorBase's exact
 * behaviour. On the server it resolves per request (see ./storeContext.ts).
 *
 * When Quasar 2 lands, dropping SSR request-scoping would only mean changing this one file.
 *
 * Vue 2.7 caveat: `reactive()` is `Vue.observable` underneath, so keys added after creation
 * are NOT reactive. Every key must be declared in `defaultState()`.
 */
export function defineState<T extends object> (name: string, defaultState: () => T) {
  /**
   * Browser: one singleton for the page's lifetime.
   */
  let clientState: T | undefined

  const isCreated = () => {
    if (process.env.SERVER) {
      return getRequestContext().states[name] !== void 0
    }

    return clientState !== void 0
  }

  const state = (): T => {
    if (process.env.SERVER) {
      const context = getRequestContext()

      if (context.states[name] === void 0) {
        context.states[name] = reactive(defaultState()) as object
      }

      return context.states[name] as T
    }

    if (clientState === void 0) {
      clientState = reactive(defaultState()) as T
    }

    return clientState
  }

  _register({
    name,
    get: () => state() as object,
    isCreated,
    // Used only by the test harness, to give each test the clean slate a fresh Vuex store gave it.
    reset: () => Object.assign(state(), defaultState())
  })

  return state
}
